import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "devextreme-react/button";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { LoadPanel } from "devextreme-react/load-panel";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { alert, confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import axios from "axios";
import PrimaryTab from "./tabs/PrimaryTab";
import { getApiErrorMessage, CONCURRENCY_HANDLED } from "../../../../common/concurrency";
import { MESSAGES } from "../../../../common/Messages";
import data from "../../../../assets/apiRoutes.json";
import { ItemKitProfileRoutes } from "../../../../const/constants";

// Add / Edit for Delphi WMD0103 "WM Item Kit Data Profile" (IMITEMKITTHEADERDP).
//
// Page skeleton mirrors weekSettings/Form.js — the dataEntryMainPage wrapper, a Toolbar header and a
// TabPanel whose "Primary" tab matches the Delphi form's own Primary tab. The listing's
// search-page-* classes are for the find screen and pushed the header below the fields when used
// here.
//
// The endpoints opt into PascalCase (DefaultContractResolver on the controller), so field names
// match the DTO exactly — same convention as Week Settings.
//
// Rule 16: the ConcurrencyToken read with the record is held in state and posted back unchanged on
// update; the 409 dialog comes free from the AppContainer axios interceptor.
const ROUTES = data.ItemKitProfile;
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const LISTING_PATH = ItemKitProfileRoutes.List;

// Delphi btnProfileDefaultsClick opened frmWMD0102 "Item Kit Codes" scoped to this profile
// (SetParameters(qryIMItemKittHeaderDP.ROWID)). That screen is migrated at
// wm/components/codes/itemKitCodes, whose find screen already filters on Profile Code.
const ITEM_KIT_CODES_PATH = "/wm/codes/item-kit-codes";

const buildInitialFields = () => ({
    RowId: EMPTY_GUID,
    RecordNumber: "",
    ProfileCode: "",
    Description: "",
    Comment: "",
    Code1: "",
    Code2: "",
    Code3: "",
    Code4: "",
    Code5: "",
    ConcurrencyToken: null,
});

const loadPanelPosition = { of: "#accordion" };

function Form(props) {
    const accessToken = props.oidc?.user?.access_token;

    const history = useHistory();
    const location = useLocation();
    const formRefs = useRef({});

    // The edit route carries the key; its absence means Add.
    const rowId = new URLSearchParams(location.search).get("id");
    const isEdit = !!rowId;

    const [formFields, setformFields] = useState(buildInitialFields());
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);

    useEffect(() => {
        if (!isEdit) return;

        setLoadPanelVisible(true);
        axios.get(
            `${window._env_.REACT_APP_APIS}/${ROUTES.GetItemKitProfile}?rowId=${rowId}`,
            { headers: { "Content-Type": "application/json", access_token: accessToken } },
        )
            .then((res) => {
                if (!res.data) {
                    alert(MESSAGES.RECORD_NOT_FOUND, MESSAGES.ERROR);
                    return;
                }
                // Spread over the initial shape so every field stays controlled even when the record
                // carries nulls.
                setformFields({ ...buildInitialFields(), ...res.data });
            })
            .catch((err) => {
                if (err?.[CONCURRENCY_HANDLED]) return;
                alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR);
            })
            .finally(() => setLoadPanelVisible(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowId, accessToken]);

    const handleFieldChange = useCallback(() => {}, []);

    const goBack = useCallback(() => {
        // Returning to the listing restores the search that brought the user here (rule 17).
        history.push(LISTING_PATH);
    }, [history]);

    /**
     * Delphi btnProfileDefaultsClick — opens the Item Kit Codes screen scoped to this profile.
     *
     * Delphi passed the profile's ROWID (SetParameters). The migrated find screen filters on Profile
     * Code rather than the row id, so the code travels in the URL and seeds that screen's Profile
     * Code search field. Same shape as itemDataProfile/Form.js goToItemDefaults, which puts the
     * profile's identity in the query string.
     */
    const goToItemKitCodes = useCallback(() => {
        const profileCode = encodeURIComponent(formFields.ProfileCode || "");
        history.push(`${ITEM_KIT_CODES_PATH}?profileCode=${profileCode}`);
    }, [history, formFields.ProfileCode]);

    const handleReset = useCallback(() => {
        setformFields((prev) => ({ ...buildInitialFields(), RowId: prev.RowId, RecordNumber: prev.RecordNumber, ConcurrencyToken: prev.ConcurrencyToken }));
    }, []);

    const save = () => {
        // The service enforces the same rule, so a direct API call is refused too.
        if (!formFields.ProfileCode || !formFields.ProfileCode.trim()) {
            alert(MESSAGES.ERROR_FORM_SUBMISSION, MESSAGES.ERROR);
            return;
        }

        const url = isEdit ? ROUTES.UpdateItemKitProfile : ROUTES.InsertItemKitProfile;
        const payload = { ...formFields, RowId: isEdit ? formFields.RowId : EMPTY_GUID };

        setLoadPanelVisible(true);
        axios.post(
            `${window._env_.REACT_APP_APIS}/${url}`,
            payload,
            { headers: { "Content-Type": "application/json", access_token: accessToken } },
        )
            .then((res) => {
                // Rule 7: green toast, then back to the listing.
                notify(res.data?.Message || MESSAGES.UPDATE_SUCCESS, "success", 3000);
                goBack();
            })
            .catch((err) => {
                // A 409 is handled by the shared interceptor, which shows the conflict dialog.
                if (err?.[CONCURRENCY_HANDLED]) return;
                alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR);
            })
            .finally(() => setLoadPanelVisible(false));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        save();
    };

    const handleDelete = () => {
        if (!isEdit) return;

        // Rule 12: project-standard confirmation wording.
        confirm(MESSAGES.DELETE_SELECTED_RECORD_CONFIRM, MESSAGES.CONFIRM).then((ok) => {
            if (!ok) return;

            setLoadPanelVisible(true);
            axios.delete(
                `${window._env_.REACT_APP_APIS}/${ROUTES.DeleteItemKitProfile}?rowId=${formFields.RowId}`,
                { headers: { "Content-Type": "application/json", access_token: accessToken } },
            )
                .then((res) => {
                    // Rule 7: a successful delete is green and returns to the listing.
                    notify(res.data?.Message || MESSAGES.DELETE_SUCCESS, "success", 3000);
                    goBack();
                })
                .catch((err) => {
                    if (err?.[CONCURRENCY_HANDLED]) return;
                    // The "profile still used by a kit header" refusal arrives as a 400 and lands here.
                    alert(getApiErrorMessage(err, MESSAGES.ERROR), MESSAGES.ERROR);
                })
                .finally(() => setLoadPanelVisible(false));
        });
    };

    const title = formFields.ProfileCode
        ? `Item Kit Profile - ${formFields.ProfileCode.trim()}`
        : "Item Kit Profile";

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={loadPanelPosition}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={handleFormSubmit}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>
                                            {title}
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    {/* Delphi enables this only once the profile exists
                                        (qeIMItemKittHeaderDPAfterScroll: not AddMode and
                                        RecordsExist; re-enabled by ApplyUpdates after a save). On a
                                        new record there is no profile to scope the codes to, so the
                                        header has to be saved first (rule 5). */}
                                    <Button
                                        className="accordianButton"
                                        text="Item Kit Codes"
                                        type="default"
                                        disabled={!isEdit}
                                        onClick={goToItemKitCodes}
                                    />
                                </Item>
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                <Item location="after">
                                    <Button
                                        className="accordianButton"
                                        onClick={handleDelete}
                                        text="Delete"
                                        type="default"
                                        disabled={!isEdit}
                                    />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>

                            <TabPanel deferRendering={false} showNavButtons={true} height={"auto"}>
                                <TabItem title="Primary">
                                    <PrimaryTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(Form);
